import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

export function FloatingPlanets() {
    const [scrollY, setScrollY] = useState(0);

    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const container = {
        show: {
            transition: {
                staggerChildren: 0.1,
            },
        },
    };

    const popIn = {
        hidden: { scale: 0, opacity: 0 },
        show: {
            scale: 1,
            opacity: 1,
            transition: {
                type: "spring" as const,
                stiffness: 500,
                damping: 20,
                mass: 0.6,
            },
        },
    };

    return (
        <div className="fixed w-full h-[5000px]">
            <motion.div
                className="absolute inset-0 pointer-events-none overflow-hidden"
                variants={container}
                initial="hidden"
                animate="show"
            >

                {/* Medium blue planet */}
                <motion.div
                    variants={popIn}
                    className="absolute w-40 h-40 rounded-full"
                    style={{
                        background: 'radial-gradient(circle at 40% 40%, #4a90e2, #1e3a8a)',
                        left: '70%',
                        top: `calc(10% + ${scrollY * 0.01}px)`,
                        animation: 'float 15s ease-in-out infinite 2s',
                        filter: 'blur(1px) brightness(50%)',
                    }}
                />

                {/* Jupiter-like gas giant */}
                <motion.div
                    variants={popIn}
                    className="absolute w-72 h-72 rounded-full overflow-hidden"
                    style={{
                        left: '15%',
                        top: `calc(5% - ${scrollY * 0.04}px)`,
                        animation: 'float 22s ease-in-out infinite 3s',
                        filter: 'blur(1px) brightness(40%)',
                        position: 'relative',
                    }}
                >
                    {/* Cloud bands */}
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            background: `
              repeating-linear-gradient(
                0deg,
                #cfa77d 0%,
                #cfa77d 6%,
                #b8895b 6%,
                #b8895b 12%,
                #e2c199 12%,
                #e2c199 18%,
                #a8744f 18%,
                #a8744f 24%
              )
            `,
                        }}
                    />

                    {/* Great Red Spot */}
                    <div
                        style={{
                            position: 'absolute',
                            top: '65%',
                            left: '50%',
                            width: '12%',
                            height: '8%',
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, #c1442e, #8a2c1d)',
                            filter: 'blur(1px)',
                            transform: 'translateX(140%)',
                            animation: 'redSpotTraverse 5s linear infinite',
                            zIndex: 3,
                        }}
                    />

                    {/* Spherical highlight */}
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: '50%',
                            background:
                                'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.25), transparent 45%)',
                            zIndex: 4,
                            pointerEvents: 'none',
                        }}
                    />

                    {/* Limb darkening */}
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: '50%',
                            background:
                                'radial-gradient(circle at center, transparent 60%, rgba(0,0,0,0.25) 100%)',
                            zIndex: 5,
                            pointerEvents: 'none',
                        }}
                    />
                </motion.div>

                <motion.div
                    variants={popIn}
                    style={{
                        width: '16rem',
                        height: '16rem',
                        transformOrigin: 'center center',
                        position: 'absolute',
                        right: '-5%',
                        top: `calc(10% + ${scrollY * 0.05}px)`,
                    }}
                >
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            animation: 'float 20s ease-in-out infinite',
                            position: 'relative',
                        }}
                    >
                        {/* Ring behind planet */}
                        <div
                            style={{
                                position: 'absolute',
                                width: '180%',
                                height: '40%',
                                left: '-40%',
                                top: '50%',
                                borderRadius: '50%',
                                transform: 'rotate(-25deg)',
                                background:
                                    'linear-gradient(90deg, rgba(255,255,255,0.05), rgba(255,255,255,0.35), rgba(255,255,255,0.05))',
                                filter: 'blur(0.5px)',
                                opacity: 0.5,
                                zIndex: 0,
                            }}
                        />

                        {/* Planet */}
                        <div
                            className="w-64 h-64 rounded-full"
                            style={{
                                position: 'relative',
                                zIndex: 1,
                                background: 'radial-gradient(circle at 30% 30%, #a569ae, #614c64)',
                                filter: 'blur(2px) brightness(50%)',
                            }}
                        />

                        {/* Ring front highlight */}
                        <div
                            style={{
                                position: 'absolute',
                                width: '180%',
                                height: '40%',
                                left: '-40%',
                                top: '50%',
                                borderRadius: '50%',
                                transform: 'rotate(-25deg)',
                                background:
                                    'linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)',
                                opacity: 0.35,
                                zIndex: 2,
                                pointerEvents: 'none',
                            }}
                        />
                    </div>
                </motion.div>

                {/* Small pink planet */}
                <motion.div
                    variants={popIn}
                    className="absolute w-32 h-32 rounded-full"
                    style={{
                        background: 'radial-gradient(circle at 35% 35%, #e94bce, #963da4)',
                        left: '15%',
                        top: `calc(15% + ${scrollY * 0.05}px)`,
                        animation: 'float 18s ease-in-out infinite 5s',
                        filter: 'blur(1.5px) brightness(50%)',
                    }}
                />



                <style>{`
        @keyframes redSpotTraverse {
          0% { transform: translateX(550%); opacity: 0; }
          10% { opacity: 0.6; }
          50% { transform: translateX(-550%); opacity: 0.6; }
          60% { opacity: 0; }
          100% { transform: translateX(-160%); opacity: 0; }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(-30px) translateX(20px); }
        }
      `}</style>
            </motion.div>
        </div>
    );
}
